import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import PercentageInput from '@components/common/PercentageInput';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import Card from '@components/common/Card';
import { useInvestmentSimulation } from '@hooks/useInvestments';
import { formatCurrency } from '@utils/formatters';

const InvestmentSimulator = () => {
  const [result, setResult] = useState(null);
  const { mutateAsync: simulate, isPending } = useInvestmentSimulation();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      initialAmount: 1000,
      monthlyContribution: 500,
      interestRate: 12,
      interestType: 'annual',
      months: 60,
      inflationRate: 4.5,
    },
  });

  const handleSimulate = async (data) => {
    const simulation = await simulate({
      initialAmount: data.initialAmount,
      monthlyContribution: data.monthlyContribution,
      interestRate: data.interestRate,
      interestType: data.interestType,
      months: parseInt(data.months),
      inflationRate: data.inflationRate || 0,
    });
    setResult(simulation);
  };

  const months = watch('months');

  // Preparar dados para gráfico
  const chartData = result?.projection?.filter((_, i) => 
    i % Math.max(1, Math.floor(result.projection.length / 24)) === 0 || 
    i === result.projection.length - 1
  ).map(p => ({
    ...p,
    monthLabel: `Mês ${p.month}`,
  })) || [];

  return (
    <div className="space-y-6">
      <Card title="🧮 Simulador de Investimento" subtitle="Calcule o rendimento do seu investimento">
        <form onSubmit={handleSubmit(handleSimulate)} className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Controller
              name="initialAmount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Valor Inicial"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  error={errors.initialAmount?.message}
                />
              )}
            />

            <Controller
              name="monthlyContribution"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Aporte Mensal"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
                  onBlur={field.onBlur}
                  error={errors.monthlyContribution?.message}
                />
              )}
            />

            <Input
              label="Período (meses)"
              type="number"
              error={errors.months?.message}
              {...register('months', { required: true, min: 1, max: 600 })}
            />

            <Select
              label="Tipo de Taxa"
              options={[
                { value: 'annual', label: 'Anual (% a.a.)' },
                { value: 'monthly', label: 'Mensal (% a.m.)' },
              ]}
              {...register('interestType')}
            />

            <Controller
              name="interestRate"
              control={control}
              render={({ field }) => (
                <PercentageInput
                  label="Taxa de Juros"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  error={errors.interestRate?.message}
                />
              )}
            />

            <Controller
              name="inflationRate"
              control={control}
              render={({ field }) => (
                <PercentageInput
                  label="Inflação Anual (opcional)"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  hint="Para calcular valor real"
                />
              )}
            />
          </div>

          <Button type="submit" isLoading={isPending} fullWidth>
            Simular Investimento
          </Button>
        </form>
      </Card>

      {result && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card padding="md">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Investido</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(result.summary.totalContributions)}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-sm text-gray-500 dark:text-gray-400">Valor Final</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(result.summary.finalBalance)}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total em Juros</p>
              <p className="text-2xl font-bold text-primary-600">
                {formatCurrency(result.summary.totalEarnings)}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-sm text-gray-500 dark:text-gray-400">Rentabilidade</p>
              <p className="text-2xl font-bold text-green-600">
                +{result.summary.returnPercentage.toFixed(2)}%
              </p>
            </Card>
          </div>

          {result.summary.realFinalBalance && (
            <Card padding="md" className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    Valor Real (descontando inflação)
                  </p>
                  <p className="text-xl font-bold text-yellow-800 dark:text-yellow-300">
                    {formatCurrency(result.summary.realFinalBalance)}
                  </p>
                </div>
                <p className="text-sm text-yellow-600 dark:text-yellow-500">
                  Perda de {formatCurrency(result.summary.finalBalance - result.summary.realFinalBalance)} pelo poder de compra
                </p>
              </div>
            </Card>
          )}

          {/* Gráfico */}
          <Card title="📈 Evolução do Patrimônio">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorContrib" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="monthLabel" stroke="#9ca3af" fontSize={12} />
                <YAxis 
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#374151' }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="totalContributions"
                  name="Total Investido"
                  stroke="#6366f1"
                  fillOpacity={1}
                  fill="url(#colorContrib)"
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  name="Valor Total"
                  stroke="#22c55e"
                  fillOpacity={1}
                  fill="url(#colorBalance)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Tabela detalhada (primeiros e últimos meses) */}
          <Card title="📊 Detalhamento" padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mês</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aporte</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rendimento</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Investido</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {/* Primeiros 6 meses */}
                  {result.projection.slice(0, 6).map((row) => (
                    <tr key={row.month}>
                      <td className="px-4 py-3 text-sm">{row.month}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(row.contribution)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(row.earnings)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(row.totalContributions)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                  {result.projection.length > 12 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-2 text-center text-sm text-gray-400">
                        ... {result.projection.length - 12} meses ...
                      </td>
                    </tr>
                  )}
                  {/* Últimos 6 meses */}
                  {result.projection.slice(-6).map((row) => (
                    <tr key={row.month}>
                      <td className="px-4 py-3 text-sm">{row.month}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(row.contribution)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(row.earnings)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(row.totalContributions)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default InvestmentSimulator;
